import { Inject, ModelEvents, Singleton } from '@denishussar/html5-game-engine';
import { Texture } from 'pixi.js';
import { ISavePictureData, SavesManager } from '../../game/Managers/SavesManager';
import { CrazyGamesBalanceManager } from '../Common/BalanceManager/CrazyGamesBalanceManager';
import { BalanceShopItemTypeEnum, IBalanceManager } from '../Common/BalanceManager/IBalanceManager';
import { EngineEvents } from '../Common/Events';
import { getSpriteFromUrl } from '../Common/getSpriteFromUrl';
import { ModalsNames } from '../Common/MadalsManager/ModalsList';
import { ModalsManager } from '../Common/MadalsManager/ModalsManager';
import {
    convertRGBA2HEXNUM,
    createBase64Image,
    createBuffer,
    createGreyTexture,
    textureToJson,
    throttle,
} from '../Common/Utils';
import { ModalContainerController } from '../Controllers/Modals/ModalContainerController';
import { AlbumsLibrary } from '../Models/AlbumsLibrary';
import { BoosterTypeEnum, GameModel, GameStateEnum, InstrumentTypeEnum } from '../Models/GameModel';
import { SoundModel } from '../Models/SoundModel';
import { IPictureData } from '../Services/CloudDB/Contract';
import { Route } from '../Utils/Helpers';
import { GameEvent, GameEventSceneButtonClick } from '../Constants/GameEvent';
import { BombBooster } from './Boosters/Bomb';
import {ILevelPlayData} from "../Services/Analytics/AnalyticsController";
import {BucketBooster} from "./Boosters/Bucket";
import { FREE_IMAGES_LIST } from '../Constants/FreeImages';

@Singleton
export class GameLogic {
    private static IMAGE_ORIGINAL = 'pictureImageRes';

    public modalsManager: ModalsManager = null;
    public balanceManager: IBalanceManager = null;
    public saveProgress = throttle(this.saveProgressNow, 1000);

    constructor(
        @Inject private model: GameModel,
        @Inject private modelEvents: ModelEvents,
        @Inject private saveManager: SavesManager,
    ) {
        const savedData = this.saveManager.getSaveGameData();

        this.model.userModel.id = savedData.userId !== null ? savedData.userId : this.model.userModel.id;
        this.model.addsCooldownTimestamp = savedData.addsCooldownTimestamp ? savedData.addsCooldownTimestamp : this.model.addsCooldownTimestamp;
        this.model.addsDay = savedData.addsDay ? savedData.addsDay : this.model.addsDay;

        this.model.userBalanceCoins = savedData.coins > -1 ? savedData.coins : this.model.userBalanceCoins;
        this.model.userBalanceBoosterLine = savedData.boosterLineCount > -1 ? savedData.boosterLineCount : this.model.userBalanceBoosterLine;
        this.model.userBalanceBoosterBrush = savedData.boosterBrushCount > -1 ? savedData.boosterBrushCount : this.model.userBalanceBoosterBrush;
        this.model.userBalanceBoosterBucket = savedData.boosterBucketCount > -1 ? savedData.boosterBucketCount : this.model.userBalanceBoosterBucket;

        this.saveManager.saveUserId(this.model.userModel.id);
        this.saveManager.saveCoins(this.model.userBalanceCoins);
        this.saveManager.saveBoosterLineCount(this.model.userBalanceBoosterLine);
        this.saveManager.saveBoosterBrushCount(this.model.userBalanceBoosterBrush);
        this.saveManager.saveBoosterBucketCount(this.model.userBalanceBoosterBucket);

        const cheatMoney = Route.searchParam('cheatMoney');
        if (cheatMoney === '1' || cheatMoney === 'true') {
            this.model.userBalanceCoins = 100000;
            this.saveManager.saveCoins(this.model.userBalanceCoins);
        }
    }

    public get isFTUE() {
        return this.saveManager.getSaveGameData().tutorialStep != -1;
    }

    public getFTUEPicture() {
        return this.model.albumLibrary.getPictureData(AlbumsLibrary.PICTURE_FTUE);
    }

    public getLastEditedPicture(): IPictureData | undefined {

        const albums = this.model.albumLibrary.getAllAlbums();
        let lastPicture: IPictureData;
        let lastSave: ISavePictureData;
        for (let i = 0; i < albums.length; i++) {
            let pictures: string[];
            // HACK for Gloria catalog sorting
            if (albums[i].id === AlbumsLibrary.GLORIA) {
                pictures = FREE_IMAGES_LIST;
            } else {
                pictures = albums[i].pictures;
            }

            for (let p = 0; p < pictures.length; p++) {
                if (!lastPicture) {
                    lastPicture = this.model.albumLibrary.getPictureData(pictures[p]);
                    lastSave = this.getSave(lastPicture.id);
                    continue;
                }

                const save = this.getSave(pictures[p]);
                if (!save || this.getPercentageProgress(save) === 100) {
                    continue;
                }

                if (!lastSave || lastSave.timestamp < save.timestamp) {
                    lastSave = save;
                    lastPicture = this.model.albumLibrary.getPictureData(pictures[p]);
                }
            }
        }

        return (lastPicture && lastSave) ? lastPicture : undefined;
    }

    public getFirstPictureForSale(): IPictureData {
        const albums = this.model.albumLibrary.getAllAlbums();

        for (let i = 0; i < albums.length; i++) {
            if (albums[i].id === AlbumsLibrary.MY_COLLECTION || albums[i].id === AlbumsLibrary.GLORIA) {
                continue;
            }
            const pictures = albums[i].pictures;
            for (let p = 0; p < pictures.length; p++) {
                const save = this.getSave(pictures[p]);
                if (!save) {
                    return this.model.albumLibrary.getPictureData(pictures[p]);
                }
            }
        }

        return undefined;
    }

    public getFreePicture(): IPictureData | undefined {
        // HACK for Gloria catalog sorting
        const album = this.model.albumLibrary.getAlbumData(AlbumsLibrary.GLORIA);
        album.pictures = FREE_IMAGES_LIST;

        for (let p = 0; p < album.pictures.length; p++) {
            const save = this.getSave(album.pictures[p]);
            if (!save || this.getPercentageProgress(save) !== 100) {
                return this.model.albumLibrary.getPictureData(album.pictures[p]);
            }
        }

        return undefined;
    }

    public setMusicOnOff(value: boolean): void {
        SoundModel.musicON(value);
        this.modelEvents.dispatch(this.model, EngineEvents.musicLoudness);
    }

    public setSoundOnOff(value: boolean): void {
        SoundModel.fxON(value);
        this.modelEvents.dispatch(this.model, EngineEvents.musicLoudness);
    }

    public initModalsManager(view: ModalContainerController) {
        this.modalsManager = new ModalsManager(view, this.model, this.modelEvents);
    }

    public initBalanceManager(classObj: any = CrazyGamesBalanceManager) {
        this.balanceManager = new classObj();
    }

    public initAnalytics(): void { //todo: refactor
    }

    public getSave(imageId: string): ISavePictureData {
        return this.saveManager.getSavePicture(imageId);
    }

    public getPercentageProgress(save?: ISavePictureData): number {
        if (save && save.indexes) {
            const transparentPixels = save.indexes.filter(v => v === -1).length;
            const filledPixels = save.indexes.filter(v => v === 1).length;
            const totalPixels = save.indexes.length;

            const fixedTotalPixels = totalPixels - transparentPixels;

            if (fixedTotalPixels === filledPixels) return 100;

            return Math.floor(100 / (totalPixels - transparentPixels) * filledPixels);
        }

        if (this.model.filledPixelsCount === 0) return 0;

        const pixelCount = this.getPixelCount();
        if (!pixelCount) {
            return 0;
        }

        return Math.floor(100 / pixelCount * this.model.filledPixelsCount);
    }

    public update(frameRate: number): void {
    }

    public setState(state: GameStateEnum): void {
        if (this.model.gameState === GameStateEnum.game) {
            this.saveProgressNow();
        }
        this.modelEvents.dispatch(this.model, 'setState', state);
        this.model.gameState = state;
    }

    public saveProgressNow() {
        console.log("SAVE PROGRESS START")
        if (!this.model.picture || !this.model.saveBuffer) {
            return;
        }

        this.model.timeSpent = this.model.timeSpent + Math.floor((Date.now() - this.model.timeStart) / 1000);
        this.model.timeStart = Date.now();

        this.saveManager.savePicture(
            this.model.picture.id,
            createBase64Image(
                this.model.saveBuffer,
                this.model.saveTexture.width,
                this.model.saveTexture.height,
            ),
            this.model.filledPixels2d,
            {
                dragCount: this.model.dragCount,
                needleCount: this.model.needleCount,
                yarnCount: this.model.yarnCount,
                stitchCount: this.model.stitchCount,
                storeCount: this.model.storeCount,
                zoomInCount: this.model.zoomInCount,
                zoomOutCount: this.model.zoomOutCount,
                timeSpent: this.model.timeSpent,
                rewardsCount: this.model.rewardsCount
            }
        );

        console.log("SAVE PROGRESS END")
    }

    // public resetProgress() {
    //     this.saveManager.savePicture(
    //         this.model.picture.id,
    //         createBase64Image(
    //             this.model.saveBuffer,
    //             this.model.saveTexture.width,
    //             this.model.saveTexture.height,
    //         ),
    //         [],
    //         {
    //             dragCount: this.model.dragCount,
    //             needleCount: this.model.needleCount,
    //             yarnCount: this.model.yarnCount,
    //             stitchCount: this.model.stitchCount,
    //             storeCount: this.model.storeCount,
    //             zoomInCount: this.model.zoomInCount,
    //             zoomOutCount: this.model.zoomOutCount,
    //         }
    //     );
    // }

    public setInstrumentType(value: InstrumentTypeEnum): void {
        this.model.instrumentType = value;
        this.modelEvents.dispatch(this.model, 'setInstrumentType', value);

        if (value === InstrumentTypeEnum.paint && this.getColorIndex() === -1) {
            this.model.paletteColorIndex = 1;
            this.model.paletteColor = this.getColor(1);
            this.modelEvents.dispatch(this.model, 'setColor', this.model.paletteColor, this.model.paletteColorIndex);
        }
    }

    public getInstrumentType(): InstrumentTypeEnum {
        return this.model.instrumentType;
    }

    public setBoosterType(value: BoosterTypeEnum): void {
        if (this.model.boosterType !== BoosterTypeEnum.none && this.model.boosterType === value) {
            this.disableBoosters();
            return;
        }

        if (value !== BoosterTypeEnum.none && this.getBoosterCount(value) === 0) {
            /*
            this.analytics.sendEvent(AnalyticsEventEnum.enterBoosterStore, {
                booster_id: value,
                picture_id: this.model.picture.id,
                user_id: this.model.userModel.id,
            });
             */

            let reason = undefined;

            if (value === BoosterTypeEnum.yarn) {
                reason = 'Out of yarn';
            } else if (value === BoosterTypeEnum.needle) {
                reason = 'Out of needle';
            } else if (value === BoosterTypeEnum.bucket) {
                reason = 'Out of bucket';
            }

            this.modalsManager.show(ModalsNames.shopCrazyGames, {withoutEvent: true, reason});
            return;
        }


        const instrument = this.getInstrumentType();
        let newInstrument = instrument;

        if (
            (instrument < InstrumentTypeEnum.hand && value !== BoosterTypeEnum.none)
            || (instrument >= InstrumentTypeEnum.hand && instrument !== InstrumentTypeEnum.booster)
        ) {
            newInstrument = InstrumentTypeEnum.booster;
        }

        // FIXME:
        let newColor = this.getColorIndex();
        switch (value) {
            case BoosterTypeEnum.yarn: {
                const cheatBall = Route.searchParam('cheatBall');

                if (cheatBall === '1' || cheatBall === 'true') {
                    this.model.boosterBallThreadSize = Math.floor(this.model.currentPictureWidth);
                } else {
                    this.model.boosterBallThreadSize = this.getBoosterBallThreadSize();
                }
                if (newColor !== -1) {
                    newColor = -1;
                    this.model.paletteColorIndex = newColor;
                    this.model.paletteColor = newColor === 0 ? -1 : this.getColor(newColor);
                    this.modelEvents.dispatch(this.model, 'setColor', this.model.paletteColor, this.model.paletteColorIndex);
                }
                break;
            }
            case BoosterTypeEnum.needle: {
                this.model.boosterNeedleSize = Math.floor(this.model.currentPictureWidth * 0.15);
                if (this.getColorIndex() === -1) {
                    newColor = this.getFirstActualColorIndices()[0] ?? -1;
                }
                break;
            }

            case BoosterTypeEnum.bucket: {
                if (this.getColorIndex() === -1) {
                    newColor = this.getFirstActualColorIndices()[0] ?? -1;
                }
                break;
            }
        }

        if (newColor != this.getColorIndex()) {
            this.model.paletteColorIndex = newColor;
            this.model.paletteColor = newColor === 0 ? -1 : this.getColor(newColor);
            this.modelEvents.dispatch(this.model, 'setColor', this.model.paletteColor, this.model.paletteColorIndex);
        }

        this.model.boosterType = value;
        this.modelEvents.dispatch(this.model, 'setBoosterType', value);

        this.setInstrumentType(newInstrument);
    }

    public getBoosterBallThreadSize() {
        return Math.floor(this.model.currentPictureWidth * this.model.boosterBallPartOfPictureWidth);
    }

    public getBoosterType(): BoosterTypeEnum {
        return this.model.boosterType;
    }

    public getBoosterCount(value: BoosterTypeEnum): number {
        let count = 0;
        switch (value) {
            case BoosterTypeEnum.yarn: {
                count = this.model.userBalanceBoosterBrush;
                break;
            }
            case BoosterTypeEnum.needle: {
                count = this.model.userBalanceBoosterLine;
                break;
            }
            case BoosterTypeEnum.hook: {
                count = this.model.userBalanceBoosterHook;
                break;
            }
            case BoosterTypeEnum.bucket: {
                count = this.model.userBalanceBoosterBucket;
                break;
            }
        }
        return count;
    }

    public setPaintEnabled(value: boolean): void {
        this.model.paintEnabled = value;
    }

    public isColored(x: number, y: number): boolean {
        try {
            return this.model.filledPixels2d[x + y * this.model.currentPictureWidth] === 1;
        } catch (error) {
            return false;
        }
    }

    public isPaintEnabled(): boolean {
        return this.model.paintEnabled;
    }

    public isCursorEnabled(): boolean {
        return this.model.paletteColorIndex === 0;
    }

    public getPalette(): number[] {
        return this.model.palette;
    }

    public setColor(index: number): void {
        const instrument = this.getInstrumentType();
        if (index === 0 && instrument !== InstrumentTypeEnum.hand) {
            this.disableBoosters();
            this.setInstrumentType(InstrumentTypeEnum.hand);
        } else if (index > 0 && instrument <= InstrumentTypeEnum.hand) {
            this.setInstrumentType(InstrumentTypeEnum.paint);
        }

        this.model.paletteColorIndex = index;
        this.model.paletteColor = index === 0 ? -1 : this.getColor(index);
        this.modelEvents.dispatch(this.model, 'setColor', this.model.paletteColor, this.model.paletteColorIndex);
    }

    public getColor(index: number = this.model.paletteColorIndex): number {
        return this.model.palette[index];
    }

    public getFirstActualColorIndices(): number[] {
        const palette = this.model.palette;
        const result = [];

        for (let i = 1; i < palette.length; i++) {
            const color = palette[i];

            if (!this.isFillComplete(color)) {
                result.push(i);
            }
        }
        return result;
    }

    public getColorIndex(color: number = this.model.paletteColor): number {
        return color === -1 ? -1 : this.model.palette.indexOf(color);
    }

    public getNextColorIndex(): number {
        const length = this.model.palette.length;
        let count = 0;
        let colorIndex = this.model.paletteColorIndex > -1 ? this.model.paletteColorIndex : 0;
        while (count < length) {
            count++;
            colorIndex++;

            if (colorIndex < 1) {
                colorIndex = 1;
            } else if (colorIndex >= length) {
                colorIndex = 1;
            }

            const color = this.getColor(colorIndex);
            if (!this.isFillComplete(color)) {
                return colorIndex;
            }
        }

        return -1;
    }

    public getPixelCount(color: number = -1): number {
        if (color > -1) {
            return this.model.filledPixelsColorTotalCount[color] || 0;
        }
        return (this.model.pixels as [number, number, number, number][]).filter(col => col[3] !== 0).length;
    }

    public getPixelColor(x: number, y: number): number {
        if (!this.model.pixels[y + this.model.texture.width * x]) {
            return -1;
        }
        const rgb = this.model.pixels[y + this.model.texture.width * x];

        if (rgb[3] === 0) {
            return -1;
        }
        return convertRGBA2HEXNUM(rgb[0], rgb[1], rgb[2]);
    }

    public fillPixelWithAnimation(x: number, y: number, color: number): void {
        this.doFillPixel(x, y, color);
        this.modelEvents.dispatch(this.model, 'fillPixelWithAnimation', x, y, color);

        if (this.isFillComplete()) {
            this.modelEvents.dispatch(this.model, 'doneColor', color, this.getColorIndex(color));
            this.modelEvents.dispatch(this.model, 'fillComplete');
            this.saveProgressNow();

            // this.modelEvents.dispatch(this.model, GameEvent.levelComplete, {
            //     levelName: this.model.picture.name,
            //     levelSize: this.model.currentPictureWidth,
            //     boosters: {
            //         needle: this.model.userBalanceBoosterLine,
            //         yarn: this.model.userBalanceBoosterBrush,
            //     },
            //     stitchCount: this.model.filledPixelsCount,
            //     dragCount: this.model.dragCount,
            //     needleCount: this.model.needleCount,
            //     yarnCount: this.model.yarnCount,
            //     zoomInCount: this.model.zoomInCount,
            //     zoomOutCount: this.model.zoomOutCount,
            //     storeCount: this.model.storeCount,
            // });

            setTimeout(() => {
                this.setState(GameStateEnum.win);
            }, 1000);
        } else if (this.isFillComplete(color)) {
            if (this.getInstrumentType() >= InstrumentTypeEnum.paint) {
                this.setColor(this.getNextColorIndex());
            }
            this.modelEvents.dispatch(this.model, 'doneColor', color, this.getColorIndex(color));
        } else {
            this.modelEvents.dispatch(this.model, 'progressColor', color, this.getColorIndex(color), this.getFillProgress(color));
        }
    }

    public fillPixelByBooster(x: number, y: number) {
        if (x < 0 ||
            y < 0 ||
            x >= this.model.saveTexture.width ||
            y >= this.model.saveTexture.height) {
            return;
        }

        const pixelColor = this.getPixelColor(x, y);

        if (pixelColor === -1) {
            return;
        }

        const currentColor = pixelColor;

        if (currentColor === pixelColor) {
            this.fillPixel(x, y, currentColor);
        }
    }

    public fillPixel(x: number, y: number, color: number): void {
        const isFilled = this.doFillPixel(x, y, color);

        this.modelEvents.dispatch(this.model, 'fillPixel', x, y, color);

        // INFO: Fix for analytics
        if (isFilled) {
            this.model.stitchCount++;
        }

        if (this.isFillComplete()) {
            this.modelEvents.dispatch(this.model, 'doneColor', color, this.getColorIndex(color));

            // console.trace("LEVEL COMPLETE")

            this.modelEvents.dispatch(this.model, GameEvent.levelComplete, {
                levelName: this.model.picture.name,
                levelSize: this.model.currentPictureWidth,
                boosters: {
                    needle: this.model.userBalanceBoosterLine,
                    yarn: this.model.userBalanceBoosterBrush,
                    bucket: this.model.userBalanceBoosterBucket
                },
                stitchCount: this.model.filledPixelsCount,
                dragCount: this.model.dragCount,
                needleCount: this.model.needleCount,
                yarnCount: this.model.yarnCount,
                zoomInCount: this.model.zoomInCount,
                zoomOutCount: this.model.zoomOutCount,
                storeCount: this.model.storeCount,
                timeSpent: this.model.timeSpent,
                rewardsCount: this.model.rewardsCount
            });

            this.saveProgressNow();
            setTimeout(() => {
                this.model.timeSpent = 0;
                this.setState(GameStateEnum.win);
            }, 1000);
        } else if (this.isFillComplete(color)) {
            this.modelEvents.dispatch(this.model, 'doneColor', color, this.getColorIndex(color));
        } else {
            this.modelEvents.dispatch(this.model, 'progressColor', color, this.getColorIndex(color), this.getFillProgress(color));
        }

        this.saveProgress();
    }

    public isFillComplete(color: number = -1): boolean {
        let result = false;
        if (color > -1) {
            result = this.model.filledPixelsColorCount[color] === this.getPixelCount(color);
        } else {
            result = this.model.filledPixelsCount === this.getPixelCount();
        }
        return result;
    }

    public getFillProgress(color: number = -1): number {
        const pixelCount = this.getPixelCount(color);
        if (!pixelCount) {
            return 0;
        }

        const result = color > -1 ?
            this.model.filledPixelsColorCount[color] / pixelCount :
            this.model.filledPixelsCount / pixelCount;

        return result ? result : 0;
    }

    public getPictureSize(): { width: number, height: number } {
        return {width: this.model.currentPictureWidth, height: this.model.currentPictureHeight};
    }

    public getPixelSize(): number {
        return GameModel.pixelSize;
    }

    public showCompletedPicture(pictureData: IPictureData): void {
        this.reset();
        this.model.picture = pictureData;
        // this.setState(GameStateEnum.achievement);
        this.setupPicture();
    }

    public setPicture(pictureData: IPictureData, props = {isSetPictureForAds: false}): void {
        this.reset();

        this.model.picture = pictureData;
        this.model.isSetPictureForAds = props.isSetPictureForAds ?? false;
        console.log('isSetPictureForAds', props.isSetPictureForAds, this.model.isSetPictureForAds);

        this.setState(GameStateEnum.game);
        this.setupPicture();
    }

    public getPicture(): IPictureData {
        return this.model.picture;
    }

    public getPictureTexture(): Texture {
        return this.model.texture;
    }

    checkPixelInCanvas(x: number, y: number) {
        return !(x < 0 ||
            y < 0 ||
            x >= this.model.saveTexture.width ||
            y >= this.model.saveTexture.height);
    }

    public disableBoosters() {
        this.setBoosterType(BoosterTypeEnum.none);
        if (this.model.activeBooster instanceof BombBooster) {
            this.setInstrumentType(InstrumentTypeEnum.hand);
        } else if (this.model.activeBooster instanceof BucketBooster) {
            this.setInstrumentType(InstrumentTypeEnum.hand);
        } else {
            this.setInstrumentType(InstrumentTypeEnum.paint);
        }

        if (this.isFillComplete(this.model.paletteColor)) {
            this.setColor(this.getNextColorIndex());
        }
    }

    public decBooster(type: BoosterTypeEnum) {
        switch (type) {
            case BoosterTypeEnum.yarn: {
                this.model.userBalanceBoosterBrush--;
                this.model.yarnCount++;

                this.saveManager.saveBoosterBrushCount(this.model.userBalanceBoosterBrush);
                this.modelEvents.dispatch(this.model, 'changeBoosterCount', type, this.model.userBalanceBoosterBrush);
                break;
            }
            case BoosterTypeEnum.needle: {
                this.model.userBalanceBoosterLine--;
                this.model.needleCount++;

                this.saveManager.saveBoosterLineCount(this.model.userBalanceBoosterLine);
                this.modelEvents.dispatch(this.model, 'changeBoosterCount', type, this.model.userBalanceBoosterLine);
                break;
            }
            case BoosterTypeEnum.hook: {
                this.model.userBalanceBoosterHook--;
                break;
            }
            case BoosterTypeEnum.bucket: {
                this.model.userBalanceBoosterBucket--;
                this.model.bucketCount++;

                this.saveManager.saveBoosterLineCount(this.model.userBalanceBoosterBucket);
                this.modelEvents.dispatch(this.model, 'changeBoosterCount', type, this.model.userBalanceBoosterBucket);
                break;
            }
        }
    }

    public addBooster(type: BoosterTypeEnum, count: number) {
        switch (type) {
            case BoosterTypeEnum.yarn: {
                this.model.userBalanceBoosterBrush += count;
                this.saveManager.saveBoosterBrushCount(this.model.userBalanceBoosterBrush);
                this.modelEvents.dispatch(this.model, 'changeBoosterCount', type, this.model.userBalanceBoosterBrush);
                break;
            }
            case BoosterTypeEnum.needle: {
                this.model.userBalanceBoosterLine += count;
                this.saveManager.saveBoosterLineCount(this.model.userBalanceBoosterLine);
                this.modelEvents.dispatch(this.model, 'changeBoosterCount', type, this.model.userBalanceBoosterLine);
                break;
            }
            case BoosterTypeEnum.hook: {
                this.model.userBalanceBoosterHook += count;
                this.modelEvents.dispatch(this.model, 'changeBoosterCount', type, this.model.userBalanceBoosterHook);
                break;
            }
            case BoosterTypeEnum.bucket: {
                this.model.userBalanceBoosterBucket += count;
                this.saveManager.saveBoosterLineCount(this.model.userBalanceBoosterBucket);
                this.modelEvents.dispatch(this.model, 'changeBoosterCount', type, this.model.userBalanceBoosterBucket);
                break;
            }
        }
    }

    public addsCooldownUpdate(day: number): void {
        this.model.addsCooldownTimestamp = Date.now();
        this.model.addsDay = day;
        this.saveManager.saveAddsCooldownTimestamp(this.model.addsCooldownTimestamp, this.model.addsDay);
    }

    public addBalanceCoins(count = 0): void {
        this.model.userBalanceCoins += count;
        this.saveManager.saveCoins(this.model.userBalanceCoins);
    }

    public getBalanceCoins(): number {
        return this.model.userBalanceCoins;
    }

    public canBuyShopItem(type: BalanceShopItemTypeEnum): boolean {
        return this.getBalanceCoins() >= this.balanceManager.getShopItemPrice(type);
    }

    public canBuyPicture(size: number): boolean {
        return this.getBalanceCoins() >= this.balanceManager.getPicturePrice(size);
    }

    public getFriendList() {  // TODO the get friends list method;
        return [
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
            {id: 'vk0', friendName: 'Ivan Ivanov', embroideryCount: 5, giftTimestamp: 1638778612460},
        ];
    }

    private setupPicture(): void {
        getSpriteFromUrl('assets/' + this.model.picture.src, 'assets/' + this.model.picture.src)
            .then((result) => {
                return Promise.resolve(result.sprite.texture);
            })
            .then((texture: PIXI.Texture) => {
                return new Promise((resolve) => {
                    this.model.texture = texture;
                    this.model.pixels = textureToJson(texture);
                    this.model.palette = [...new Set([-1, ...new Set<number>(this.model.pixels.map(pixel => pixel[3] !== 0 ? convertRGBA2HEXNUM(pixel[0], pixel[1], pixel[2]) : -1))])];

                    // reset stats
                    this.model.timeSpent = 0;
                    this.model.timeStart = Date.now();

                    const w = texture.width;
                    const h = texture.height;

                    this.setPictureSize(w, h);
                    this.model.filledPixels2d = new Array(w * h).fill(0);

                    for (let x = 0; x < w; x++) {
                        for (let y = 0; y < h; y++) {
                            const color = this.getPixelColor(x, y);
                            const rgb = this.model.pixels[y + this.model.texture.width * x];

                            if (rgb[3] === 0) {
                                this.model.filledPixels2d[x + y * this.model.currentPictureWidth] = -1;
                            }
                            if (color === -1) continue;

                            if (!this.model.filledPixelsColorTotalCount[color]) {
                                this.model.filledPixelsColorTotalCount[color] = 0;
                            }
                            this.model.filledPixelsColorTotalCount[color] += 1;
                        }
                    }

                    this.model.saveTexture = createGreyTexture(this.model.texture);

                    this.model.pictureBuffer = createBuffer(this.model.texture);
                    this.model.saveBuffer = createBuffer(this.model.saveTexture);

                    this.modelEvents.dispatch(this.model, 'onTextureCreated');

                    const saveData = this.saveManager.getSavePicture(this.model.picture.id);

                    const eventName = saveData ? GameEvent.levelContinue : GameEvent.levelStart;
                    const eventData: ILevelPlayData = {
                        levelName: this.model.picture.name,
                        levelSize: this.model.currentPictureWidth,
                        boosters: {
                            needle: this.model.userBalanceBoosterLine,
                            yarn: this.model.userBalanceBoosterBrush,
                            bucket: this.model.userBalanceBoosterBucket
                        },
                        percentage: this.getPercentageProgress(saveData),

                        stitchCount: saveData?.statistics?.stitchCount ?? 0,
                        dragCount: saveData?.statistics?.dragCount ?? 0,
                        needleCount: saveData?.statistics?.needleCount ?? 0,
                        yarnCount: saveData?.statistics?.yarnCount ?? 0,
                        zoomInCount: saveData?.statistics?.zoomInCount ?? 0,
                        zoomOutCount: saveData?.statistics?.zoomOutCount ?? 0,
                        storeCount: saveData?.statistics?.storeCount ?? 0,
                        timeSpent: saveData?.statistics?.timeSpent ?? 0,
                        rewardsCount: saveData?.statistics?.rewardsCount ?? 0,
                    }
                    this.modelEvents.dispatch(this.model, eventName, eventData);
                    this.model.timeStart = Date.now();

                    if (!saveData) {
                        this.saveProgressNow();
                    }

                    /*
                    const saveData = this.saveManager.getSavePicture(this.model.picture.id);

                    if (saveData && saveData.indexes) {
                        this.analytics.sendEvent(AnalyticsEventEnum.continuePlay, {
                            picture_id: this.model.picture.id,
                            picture_completion: this.getPercentageProgress(saveData).toString(),
                            user_id: this.model.userModel.id,
                        });

                    } else {
                        this.analytics.sendEvent(AnalyticsEventEnum.startPlay, {
                            picture_id: this.model.picture.id,
                            user_id: this.model.userModel.id,
                        });
                    }
                     */

                    setTimeout(() => this.fillSavedPixels(), 120);
                });
            })
            .catch((error) => {
                console.log('GameLogic.setupPicture ERROR', error);
            });
    }

    private reset(): void {
        this.model.isSetPictureForAds = false;

        this.model.texture = null;
        this.model.pictureBuffer = null;
        PIXI.Texture.removeFromCache(this.model.texture);
        PIXI.Texture.removeFromCache(this.model.saveTexture);
        if (this.model.saveTexture) {
            this.model.saveTexture.destroy(true);
        }
        this.model.saveTexture = null;
        this.model.saveBuffer = null;
        this.model.pixels = [];
        this.model.palette = [];
        this.model.picture = null;

        /**
         * удаляем ресурсы из лоадера, для того, чтобы подгрузить с этими же
         * именами ресурсы при инициализации GameScreenController
         */
        delete PIXI.loader.resources[GameLogic.IMAGE_ORIGINAL];

        this.model.filledPixels = [];
        this.model.filledPixelsCount = 0;
        this.model.filledPixelsColorCount = [];
        this.model.filledPixels2d = [];
        this.model.filledPixelsColorTotalCount = [];

        this.model.instrumentType = InstrumentTypeEnum.hand;
        this.model.boosterType = BoosterTypeEnum.none;

        this.model.paintEnabled = false;
        this.model.paletteColor = 0;
        this.model.paletteColorIndex = 0;

        this.model.boosterBallThreadEnabled = false;
        this.model.boosterNeedleEnabled = false;

        this.model.stitchCount = 0;
        this.model.dragCount = 0;
        this.model.needleCount = 0;
        this.model.yarnCount = 0;
        this.model.zoomInCount = 0;
        this.model.zoomOutCount = 0;
        this.model.storeCount = 0;
        this.model.rewardsCount = 0;
    }

    private fillSavedPixels(): void {
        const saveData = this.saveManager.getSavePicture(this.model.picture.id);
        if (saveData && saveData.indexes) {
            for (let i = 0; i < saveData.indexes.length; i++) {
                if (saveData.indexes[i] === 1) {
                    const y = Math.floor(i / this.model.currentPictureWidth) || 0;
                    const x = Math.floor(i % this.model.currentPictureWidth) || 0;
                    const color = this.getPixelColor(x, y);
                    this.doFillPixel(x, y, color);

                    if (this.isFillComplete(color)) {
                        this.modelEvents.dispatch(this.model, 'doneColor', color, this.getColorIndex(color));
                    } else {
                        this.modelEvents.dispatch(this.model, 'progressColor', color, this.getColorIndex(color), this.getFillProgress(color));
                    }
                    this.modelEvents.dispatch(this.model, 'fillPixel', x, y, color);
                }
            }
        }

        if (saveData && saveData.statistics) {
            for (let statisticsKey in saveData.statistics) {
                this.model[statisticsKey] = saveData.statistics[statisticsKey];
            }
        }

        this.modelEvents.dispatch(this.model, 'onImageReadyForPaint');
    }

    private doFillPixel(x: number, y: number, color: number): boolean {
        const pixelIndex = y * this.model.saveTexture.width + x;
        let isFilled = false;

        this.model.saveBuffer[pixelIndex * 4] = this.model.pictureBuffer[pixelIndex * 4];
        this.model.saveBuffer[pixelIndex * 4 + 1] = this.model.pictureBuffer[pixelIndex * 4 + 1];
        this.model.saveBuffer[pixelIndex * 4 + 2] = this.model.pictureBuffer[pixelIndex * 4 + 2];

        if (!this.model.filledPixels[x]) {
            this.model.filledPixels[x] = [];
        }

        if (!this.model.filledPixels[x][y]) {
            this.model.filledPixels[x][y] = color;
            this.model.filledPixelsCount++;
            isFilled = true;

            if (!this.model.filledPixelsColorCount[color]) {
                this.model.filledPixelsColorCount[color] = 0;
            }
            this.model.filledPixelsColorCount[color]++;
        }
        this.model.filledPixels2d[x + y * this.model.currentPictureWidth] = 1;

        return isFilled;
    }

    private setPictureSize(w: number, h: number) {
        this.model.currentPictureWidth = w;
        this.model.currentPictureHeight = h;
    }
}
